import { useEffect, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import classNames from 'classnames';
import { MentionsInput as Mentions, Mention } from 'react-mentions';
import OutsideClickHandler from 'react-outside-click-handler';

import {
  CloseIcon,
  CloseBoldIcon,
  ExclamationTriangleIcon,
  ImageIcon,
  RepromptIcon,
  SerifXIcon,
} from '../../icons';

import Checkbox from '../Checkbox';
import ErrorText from '../ErrorText';
import OverlayTriggerTooltip from '../OverlayTriggerTooltip';
import SuggestionsWrapper from '../MentionsInput/components/SuggestionsWrapper';

import styles from './_index.module.scss';

export interface RichTextInputProps {
  autoFocus?: boolean;
  className?: string;
  error?: string;
  hasConceptImage?: boolean;
  hasConceptName?: boolean;
  hasImageError?: boolean;
  id?: string;
  imageUrl?: string;
  isDisabled?: boolean;
  isExpanded?: boolean;
  isImageLoading?: boolean;
  isSingleLine?: boolean;
  label?: React.ReactNode;
  maxImageSize?: number;
  mentions?: Array<any>;
  onChange?: (value: string) => void;
  onImageRemove?: () => void;
  onImageUpload?: (file: File) => Promise<void>;
  onPaste?: (
    event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    pastedValue: string
  ) => void;
  placeholder?: string;
  setHasImageError?: (hasError: boolean) => void;
  setShouldShowConceptImage?: () => void;
  shouldShowConceptImage?: boolean;
  suggestions?: Record<string, any>;
  value: string;
}

const RichTextInput = ({
  autoFocus,
  className,
  error,
  hasConceptImage = false,
  hasConceptName = false,
  hasImageError,
  id = nanoid(),
  imageUrl,
  isDisabled,
  isExpanded,
  isImageLoading,
  isSingleLine = true,
  label,
  maxImageSize,
  mentions = [],
  onChange = () => {},
  onImageRemove,
  onImageUpload,
  onPaste,
  setHasImageError,
  setShouldShowConceptImage,
  shouldShowConceptImage,
  suggestions = {},
  value = '',
  ...props
}: RichTextInputProps) => {
  const [imageErrorText, setImageErrorText] = useState<string>('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isAddImageDisabled, setIsAddImageDisabled] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, []);

  useEffect(() => {
    if (!hasConceptImage) return;

    if (shouldShowConceptImage) {
      setIsAddImageDisabled(true);
    } else {
      setIsAddImageDisabled(false);
    }
  }, [hasConceptImage, shouldShowConceptImage]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];

      if (maxImageSize && file.size > maxImageSize) {
        setHasImageError?.(true);
        setImageErrorText(`File size exceeds ${maxImageSize / (1024 * 1024)}MB limit`);
        return;
      }

      if (onImageUpload) {
        try {
          await onImageUpload(file);
        } catch (error) {
          return;
        }
      }
      setFile(file);
    }
  };

  const handleFileRemove = () => {
    if (onImageRemove) {
      onImageRemove();
    }
    setFile(undefined);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleKeyDown = ({ code }: React.KeyboardEvent<HTMLInputElement>) => {
    if (isDisabled) {
      return;
    }
    if (code === 'Tab') {
      setIsFocused(false);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (onPaste) {
      // @ts-ignore – window.clipboardData is for IE support, should be updated
      const value = (event.clipboardData || window.clipboardData).getData('text');
      onPaste(event, value);
    }
  };

  const openMentionsProgrammatically = (trigger: string) => {
    if (!inputRef.current || !trigger) return;

    const newValue = value.split('');
    const selectionStart = inputRef.current.selectionStart ?? value.length;
    const selectionEnd = inputRef.current.selectionEnd ?? value.length;

    if (newValue[selectionStart - 1] && newValue[selectionStart - 1] !== ' ') {
      trigger = ' ' + trigger;
    }

    let spaceAfter = '';
    if (newValue.length > selectionEnd && newValue[selectionEnd] !== ' ') {
      spaceAfter = ' ';
    }

    newValue.splice(selectionStart, selectionEnd - selectionStart, trigger, spaceAfter);
    onChange(newValue.join(''));

    setTimeout(() => {
      if (!inputRef.current) return;
      inputRef.current.selectionStart = inputRef.current.selectionEnd =
        selectionStart + trigger.length;
      inputRef.current.focus({ preventScroll: true });

      // Add keyboard event to trigger the mentions dropdown
      const keydownEvent = new KeyboardEvent('keydown', {
        bubbles: true,
        code: 'ArrowDown',
        key: 'ArrowDown',
      });
      inputRef.current.dispatchEvent(keydownEvent);
    }, 100);
  };

  const insertMap = {
    concept_name: '[concept_name]',
  };

  const handleAddConceptDetails = (type: keyof typeof insertMap) => {
    const insertText = insertMap[type];
    if (!insertText || !inputRef.current) return;

    const selectionStart = inputRef.current.selectionStart ?? value.length;
    const selectionEnd = inputRef.current.selectionEnd ?? value.length;
    const before = value.slice(0, selectionStart);
    const after = value.slice(selectionEnd);
    const newValue = before + insertText + after;

    onChange(newValue);

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.selectionStart = inputRef.current.selectionEnd =
          selectionStart + insertText.length;
        inputRef.current.focus({ preventScroll: true });
      }
    }, 0);
  };

  return (
    <div
      className={classNames(styles['rich-text-input'], {
        [styles['is-disabled']]: isDisabled,
      })}
      onFocus={() => setIsFocused(true)}
      onKeyDown={handleKeyDown}
    >
      {label && (
        <label className={styles['rich-text-input-label']} htmlFor={id}>
          {label}
        </label>
      )}
      <OutsideClickHandler onOutsideClick={() => setIsFocused(false)}>
        <div
          className={classNames(styles['rich-text-input-container'], {
            [styles['is-focused']]: isFocused,
          })}
        >
          {(isExpanded || isFocused) && (
            <>
              <div className={styles['rich-text-input-tools']}>
                {hasConceptName && (
                  <div className={styles['rich-text-concept-tools']}>
                    {hasConceptName && (
                      <OverlayTriggerTooltip content={isDisabled ? null : 'Insert Concept Name'}>
                        <button
                          aria-label="Insert Concept Name"
                          disabled={isDisabled}
                          onClick={() => handleAddConceptDetails('concept_name')}
                        >
                          <SerifXIcon />
                          <span>Concept Name</span>
                        </button>
                      </OverlayTriggerTooltip>
                    )}
                  </div>
                )}
                <OverlayTriggerTooltip
                  content={
                    isDisabled
                      ? null
                      : isAddImageDisabled
                        ? 'Only one image type allowed per question.'
                        : 'Add Image'
                  }
                >
                  <label
                    aria-disabled={isDisabled}
                    aria-label="Add Image"
                    htmlFor={`${id}-file-input`}
                    role="button"
                  >
                    <ImageIcon />
                    <span>Add an Image</span>
                  </label>
                  <input
                    accept="image/gif, image/jpeg, image/png"
                    aria-hidden
                    className={styles['rich-text-input-file']}
                    disabled={isDisabled || isAddImageDisabled}
                    id={`${id}-file-input`}
                    onChange={handleFileChange}
                    tabIndex={-1}
                    type="file"
                  />
                </OverlayTriggerTooltip>
                {mentions.map((mentionConfig: any, index: number) => (
                  <OverlayTriggerTooltip
                    content={isDisabled ? null : mentionConfig.label}
                    key={index}
                  >
                    <button
                      aria-label={mentionConfig.label}
                      disabled={isDisabled}
                      onClick={() => openMentionsProgrammatically(mentionConfig.trigger)}
                    >
                      {mentionConfig.icon}
                      <span>{mentionConfig.label}</span>
                    </button>
                  </OverlayTriggerTooltip>
                ))}
              </div>
              <div className={styles['rich-text-input-divider']} />
            </>
          )}
          {mentions.length > 0 ? (
            <Mentions
              className="mentions"
              classNames={styles}
              customSuggestionsContainer={(children) => (
                <SuggestionsWrapper children={children} {...suggestions} />
              )}
              disabled={isDisabled}
              id={id}
              inputRef={inputRef}
              onChange={(_, __, newPlainTextValue) => onChange(newPlainTextValue)}
              onPaste={handlePaste}
              singleLine={isSingleLine}
              value={value}
              {...props}
            >
              {mentions.map(({ icon: _, label: __, ...mentionConfig }, index) => (
                <Mention key={index} {...mentionConfig} />
              ))}
            </Mentions>
          ) : (
            <input
              disabled={isDisabled}
              id={id}
              onChange={handleInputChange}
              onPaste={handlePaste}
              ref={inputRef}
              type="text"
              value={value}
              {...props}
            />
          )}

          {hasConceptImage && (
            <div className={styles['rich-text-input-image-container']}>
              <Checkbox
                isChecked={shouldShowConceptImage}
                isDisabled={file != null || imageUrl != null}
                label="Show Concept Image"
                onChange={setShouldShowConceptImage}
                value={shouldShowConceptImage}
              />
            </div>
          )}

          {(hasImageError || isImageLoading || file || imageUrl) && (
            <div className={styles['rich-text-input-image-container']}>
              {hasImageError && (
                <div className={styles['rich-text-input-image-error']}>
                  <div className={styles['rich-text-input-image-error-text']}>
                    <ExclamationTriangleIcon /> Image upload failed:{' '}
                    {imageErrorText || 'please try again'}.
                  </div>
                  <div
                    className={styles['rich-text-input-image-error-close-icon']}
                    onClick={() => {
                      setHasImageError?.(false);
                      setImageErrorText('');
                    }}
                  >
                    <CloseBoldIcon />
                  </div>
                </div>
              )}
              {isImageLoading && (
                <div className={styles['rich-text-input-image-loading']}>
                  <RepromptIcon /> Image is uploading...
                </div>
              )}
              {!isImageLoading && !hasImageError && (file || imageUrl) && (
                <div className={styles['rich-text-input-image-preview']}>
                  <img alt="Uploaded File" src={file ? URL.createObjectURL(file) : imageUrl} />
                  <div className={styles['rich-text-input-remove-image-container']}>
                    <OverlayTriggerTooltip content="Remove Image">
                      <div
                        className={styles['rich-text-input-remove-image-button']}
                        onClick={handleFileRemove}
                      >
                        <CloseIcon className={styles['rich-text-input-remove-image-icon']} />
                      </div>
                    </OverlayTriggerTooltip>
                  </div>
                  <div className={styles['rich-text-input-image-preview-overlay']} />
                </div>
              )}
            </div>
          )}
        </div>
      </OutsideClickHandler>
      {error && <ErrorText className={styles['rich-text-input-error']}>{error}</ErrorText>}
    </div>
  );
};

export default RichTextInput;
